import {

  Component,

  ElementRef,

  HostListener,

  OnDestroy,

  OnInit,

  ViewChild,

} from '@angular/core';

import { Router, NavigationEnd } from '@angular/router';

import { Subscription } from 'rxjs';

import { filter } from 'rxjs/operators';

import {

  EcommerceOnboardingService,

  OnboardingTourStep,

  TourGateState,

  CatalogSyncStatus,

} from '../../services/ecommerce-onboarding.service';

import { AuthService } from 'src/app/features/services/auth/auth.service';



interface SpotlightRect {

  top: number;

  left: number;

  width: number;

  height: number;

}



interface BlockerPanel extends SpotlightRect {}



@Component({

  selector: 'app-onboarding-tour',

  templateUrl: './onboarding-tour.component.html',

  styleUrls: ['./onboarding-tour.component.scss'],

})

export class OnboardingTourComponent implements OnInit, OnDestroy {

  @ViewChild('tooltipCard') tooltipCard?: ElementRef<HTMLElement>;



  showOverlay = false;

  currentStep: OnboardingTourStep | null = null;

  currentIndex = 0;

  totalSteps = 0;

  spotlight: SpotlightRect | null = null;

  blockerPanels: BlockerPanel[] = [];

  cardStyle: Record<string, string> = {};

  progressSteps: number[] = [];

  gateState: TourGateState = { canProceed: true, message: null };

  catalogSyncStatus: CatalogSyncStatus = 'idle';

  displayTotalSteps = 0;

  activationTourCommitted = false;

  // Catalog upload alert temporarily disabled
  // catalogAlertMessage = '';
  // catalogAlertDisplayed = false;
  // catalogAlertLeaving = false;
  // private catalogLeaveTimer: ReturnType<typeof setTimeout> | null = null;
  // private readonly catalogLeaveDurationMs = 380;



  get allowPageInteraction(): boolean {

    return !!this.currentStep?.allowPageInteraction;

  }



  get backdropPassThrough(): boolean {

    return this.isActivationSuccessMode;

  }



  /** Block clicks outside spotlight; only spotlight hole + tour card stay interactive. */
  get useSpotlightBlocker(): boolean {

    return !!this.spotlight && !this.isActivationSuccessMode;

  }



  get useFullBackdrop(): boolean {

    return this.showOverlay && !this.useSpotlightBlocker && !this.backdropPassThrough;

  }



  get isActivationSuccessMode(): boolean {

    return (

      this.currentStep?.id === 'bot-activation' &&

      this.activationTourCommitted

    );

  }



  get tourCardTitle(): string {

    if (this.isActivationSuccessMode) {

      return 'Bot activated!';

    }

    return this.currentStep?.title ?? '';

  }



  get tourCardDescription(): string {

    if (this.isActivationSuccessMode) {

      return 'Save your settings to complete setup and go live on your channel.';

    }

    return this.currentStep?.description ?? '';

  }



  get primaryActionLabel(): string {

    if (this.isActivationSuccessMode) {

      return 'Save & Finish';

    }

    return this.displayStepNumber >= this.displayTotalSteps ? 'Finish' : 'Next';

  }



  get canClickPrimary(): boolean {

    if (this.currentStep?.id === 'bot-activation' && !this.activationTourCommitted) {

      return false;

    }

    if (this.isActivationSuccessMode) {

      return this.canProceed;

    }

    return this.canProceed;

  }



  get showActivateFirstHint(): boolean {

    return (

      this.currentStep?.id === 'bot-activation' &&

      !this.activationTourCommitted &&

      this.canProceed &&

      !this.showCatalogStatus

    );

  }



  // Catalog upload alert temporarily disabled
  /*
  get catalogAlertVisible(): boolean {

    return (

      this.auth.isAdmin() &&

      this.catalogAlertDisplayed &&

      !this.onboarding.isActivationTourActive()

    );

  }



  get catalogAlertFailed(): boolean {

    return this.catalogSyncStatus === 'failed';

  }



  get catalogAlertTitle(): string {

    return this.catalogAlertFailed

      ? 'Catalog upload failed'

      : 'Uploading your product catalog';

  }



  get catalogAlertSubtitle(): string {

    if (this.catalogAlertFailed) {

      return (

        this.catalogAlertMessage ||

        'Something went wrong while uploading your catalog. Please try again.'

      );

    }

    return 'Please wait before activating your bot.';

  }
  */



  get displayStepNumber(): number {

    return this.currentStep?.stepNumber ?? this.currentIndex + 1;

  }



  get canProceed(): boolean {

    return this.gateState.canProceed;

  }



  get gateMessage(): string | null {

    return this.gateState.message;

  }



  get showCatalogStatus(): boolean {

    return (

      this.currentStep?.gate === 'catalog-ready' &&

      !this.canProceed &&

      (this.catalogSyncStatus === 'loading' || this.catalogSyncStatus === 'failed')

    );

  }



  get showStandardHint(): boolean {

    return !!this.gateMessage && !this.showCatalogStatus;

  }



  get catalogSyncFailed(): boolean {

    return this.catalogSyncStatus === 'failed';

  }



  get catalogStatusTitle(): string {

    return this.catalogSyncFailed

      ? 'Catalog upload failed'

      : 'Uploading product catalog';

  }



  get catalogStatusMessage(): string {

    return this.catalogSyncFailed

      ? 'Fix the catalog issue, then finish activation.'

      : 'Activation unlocks automatically once the upload completes.';

  }



  private subs = new Subscription();

  private repositionTimer: ReturnType<typeof setTimeout> | null = null;

  private locateTimer: ReturnType<typeof setTimeout> | null = null;

  private scrollLockState: {

    scrollY: number;

    htmlOverflow: string;

    bodyOverflow: string;

    bodyPosition: string;

    bodyTop: string;

    bodyWidth: string;

    lockedElements: Array<{ el: HTMLElement; overflow: string }>;

  } | null = null;

  private readonly wheelListener = (event: WheelEvent) => this.blockScrollEvent(event);

  private readonly touchMoveListener = (event: TouchEvent) => this.blockScrollEvent(event);

  private readonly scrollContainerSelectors = [

    '#content2',

    '.sidebar-scrollarea',

    '#settingsStore',

    'mat-sidenav-content',

    '.integration-steps-panel',

  ];



  constructor(

    private onboarding: EcommerceOnboardingService,

    private router: Router,

    private auth: AuthService,

  ) {}



  ngOnInit(): void {

    this.syncStepMeta(this.onboarding.steps);



    this.subs.add(

      this.onboarding.activeSteps$.subscribe((steps) => {

        this.syncStepMeta(steps);

        if (this.showOverlay) {

          this.currentStep = steps[this.currentIndex] ?? null;

          this.scheduleReposition();

        }

      }),

    );



    this.subs.add(

      this.onboarding.tourActive$.subscribe(() => {

        this.updateOverlayVisibility();

      }),

    );



    this.subs.add(

      this.router.events

        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))

        .subscribe(() => {

          this.updateOverlayVisibility();

        }),

    );



    this.updateOverlayVisibility();



    this.subs.add(

      this.onboarding.currentStepIndex$.subscribe((index) => {

        this.currentIndex = index;

        this.currentStep = this.onboarding.steps[index] ?? null;

        this.displayTotalSteps = this.onboarding.effectiveTotalSteps;

        if (this.showOverlay) {

          this.prepareViewportForCurrentStep();

          this.scheduleReposition();

        }

        this.onboarding.refreshTourGateState();

      }),

    );



    this.subs.add(

      this.onboarding.tourGateState$.subscribe((state) => {

        this.gateState = state;

      }),

    );



    this.subs.add(

      this.onboarding.catalogSyncStatus$.subscribe((status) => {

        this.catalogSyncStatus = status;

        // this.onCatalogAlertStatusChange(status);
        if (this.onboarding.tourActive$.value) {

          this.onboarding.refreshTourGateState();

        }

      }),

    );



    // Catalog upload alert temporarily disabled
    /*
    this.subs.add(

      this.onboarding.catalogSyncMessage$.subscribe((message) => {

        this.catalogAlertMessage = message;

      }),

    );
    */



    this.subs.add(

      this.onboarding.activationTourCommitted$.subscribe((committed) => {

        this.activationTourCommitted = committed;

        if (this.showOverlay && committed) {

          this.spotlight = null;

          this.blockerPanels = [];

          const anchor = document.querySelector('[data-onboarding="bot-activation"]');

          if (anchor instanceof HTMLElement) {

            this.applyActivationSuccessLayout(anchor);

            setTimeout(() => this.applyActivationSuccessLayout(anchor), 80);

          } else {

            this.applyActivationSuccessLayout();

          }

        }

      }),

    );

  }



  private syncStepMeta(steps: OnboardingTourStep[]): void {

    this.totalSteps = steps.length;

    this.displayTotalSteps = this.onboarding.effectiveTotalSteps;

    this.progressSteps = Array.from({ length: this.displayTotalSteps }, (_, i) => i);

  }



  private updateOverlayVisibility(): void {

    const shouldShow = this.onboarding.shouldShowTourOverlay(this.router.url);



    if (!shouldShow) {

      if (this.showOverlay) {

        this.showOverlay = false;

        this.unlockPageScroll();

        this.spotlight = null;

        this.blockerPanels = [];

        this.cardStyle = {};

      }

      return;

    }



    if (!this.showOverlay) {

      this.showOverlay = true;

      this.currentStep = this.onboarding.steps[this.currentIndex] ?? null;

      this.lockPageScroll();

    }



    this.scheduleReposition();

  }



  ngOnDestroy(): void {

    this.subs.unsubscribe();

    this.clearTimers();

    this.unlockPageScroll();

  }



  @HostListener('window:resize')

  onViewportChange(): void {

    if (this.showOverlay) {

      this.scheduleReposition();

    }

  }



  @HostListener('document:keydown', ['$event'])

  onDocumentKeyDown(event: KeyboardEvent): void {

    if (!this.showOverlay) {

      return;

    }

    const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];

    if (scrollKeys.includes(event.key)) {

      event.preventDefault();

    }

  }



  next(): void {

    if (this.isActivationSuccessMode) {

      this.onboarding.requestActivationSaveAndFinish();

      return;

    }

    this.onboarding.nextStep();

  }



  previous(): void {

    this.onboarding.previousStep();

  }



  skip(): void {

    this.onboarding.skipTour();

  }



  private scheduleReposition(): void {

    if (this.repositionTimer) {

      clearTimeout(this.repositionTimer);

    }

    const delay = 80;

    this.repositionTimer = setTimeout(() => this.reposition(), delay);

  }



  private reposition(): void {

    if (!this.currentStep) {

      this.spotlight = null;

      return;

    }



    if (this.currentStep.prepareAction === 'open-workspace-panel') {

      this.onboarding.prepareStep$.next(this.currentStep);

    }



    this.locateAnchor(0);

  }



  private locateAnchor(attempt: number): void {

    if (this.locateTimer) {

      clearTimeout(this.locateTimer);

      this.locateTimer = null;

    }



    if (!this.currentStep) {

      return;

    }



    const anchor = this.findVisibleAnchor(this.currentStep.anchorSelector);



    const rect = anchor?.getBoundingClientRect();

    const isVisible =

      !!anchor &&

      !!rect &&

      rect.width > 0 &&

      rect.height > 0 &&

      rect.bottom > 0 &&

      rect.right > 0 &&

      rect.top < window.innerHeight &&

      rect.left < window.innerWidth;



    if (!isVisible && attempt < 20) {

      this.locateTimer = setTimeout(() => this.locateAnchor(attempt + 1), 220);

      return;

    }



    if (!anchor) {

      this.applyFallbackLayout();

      return;

    }



    if (!isVisible) {

      this.applyLayout(anchor);

      return;

    }



    requestAnimationFrame(() => {

      requestAnimationFrame(() => {

        this.applyLayout(anchor);

        requestAnimationFrame(() => this.applyLayout(anchor));

      });

    });

  }



  /** Position spotlight on anchor and tour card directly underneath it. */
  private applyLayout(anchor: HTMLElement): void {

    if (this.currentStep?.id === 'bot-activation' && this.activationTourCommitted) {

      this.applyActivationSuccessLayout(anchor);

      return;

    }

    this.placeCardUnderAnchor(anchor, 12, true);

  }



  /** After bot is toggled ON: no spotlight, card stays under activation row. */
  private applyActivationSuccessLayout(anchor?: HTMLElement): void {

    this.spotlight = null;

    this.blockerPanels = [];

    if (anchor) {

      this.placeCardUnderAnchor(anchor, 12, false);

      return;

    }

    const viewportPad = 16;

    const footerReserve = 24;

    const cardWidth = Math.min(420, window.innerWidth - viewportPad * 2);

    const cardHeight = this.tooltipCard?.nativeElement?.offsetHeight ?? 220;

    const top = Math.max(viewportPad, window.innerHeight - cardHeight - footerReserve);

    const left = Math.max(viewportPad, (window.innerWidth - cardWidth) / 2);



    this.cardStyle = {

      top: `${top}px`,

      left: `${left}px`,

      width: `${cardWidth}px`,

    };

  }



  private placeCardUnderAnchor(anchor: HTMLElement, gap: number, showSpotlight: boolean): void {

    const anchorRect = anchor.getBoundingClientRect();

    const padding = 12;

    const viewportPad = 16;



    if (showSpotlight) {

      this.spotlight = {

        top: anchorRect.top - padding,

        left: anchorRect.left - padding,

        width: anchorRect.width + padding * 2,

        height: anchorRect.height + padding * 2,

      };

      this.syncBlockerPanels(this.spotlight);

    } else {

      this.spotlight = null;

      this.blockerPanels = [];

    }



    const cardEl = this.tooltipCard?.nativeElement;

    const cardHeight = cardEl?.offsetHeight ?? 280;

    const cardWidth = Math.min(

      Math.max(anchorRect.width, 320),

      window.innerWidth - viewportPad * 2,

    );

    let left = anchorRect.left + (anchorRect.width - cardWidth) / 2;

    let top = anchorRect.bottom + gap;



    if (top + cardHeight > window.innerHeight - viewportPad) {

      top = Math.max(viewportPad, anchorRect.top - cardHeight - gap);

    }



    left = Math.max(viewportPad, Math.min(left, window.innerWidth - cardWidth - viewportPad));



    this.cardStyle = {

      top: `${top}px`,

      left: `${left}px`,

      width: `${cardWidth}px`,

    };

  }



  /** Keep tour card visible when anchor is not yet in the viewport. */
  private applyFallbackLayout(): void {

    this.spotlight = null;

    this.blockerPanels = [];

    const viewportPad = 16;

    const cardWidth = Math.min(420, window.innerWidth - viewportPad * 2);

    this.cardStyle = {

      top: `${Math.max(viewportPad, window.innerHeight * 0.18)}px`,

      left: `${Math.max(viewportPad, (window.innerWidth - cardWidth) / 2)}px`,

      width: `${cardWidth}px`,

    };

  }



  private syncBlockerPanels(spotlight: SpotlightRect): void {

    const bottom = spotlight.top + spotlight.height;

    const right = spotlight.left + spotlight.width;

    const vw = window.innerWidth;

    const vh = window.innerHeight;



    this.blockerPanels = [

      { top: 0, left: 0, width: vw, height: Math.max(0, spotlight.top) },

      {

        top: spotlight.top,

        left: 0,

        width: Math.max(0, spotlight.left),

        height: spotlight.height,

      },

      {

        top: spotlight.top,

        left: right,

        width: Math.max(0, vw - right),

        height: spotlight.height,

      },

      {

        top: bottom,

        left: 0,

        width: vw,

        height: Math.max(0, vh - bottom),

      },

    ];

  }



  private findVisibleAnchor(selector: string): HTMLElement | null {

    const nodes = Array.from(document.querySelectorAll(selector));

    let best: HTMLElement | null = null;

    let bestArea = 0;



    for (const node of nodes) {

      if (!(node instanceof HTMLElement)) {

        continue;

      }

      const rect = node.getBoundingClientRect();

      if (rect.width <= 0 || rect.height <= 0) {

        continue;

      }

      const visibleTop = Math.max(rect.top, 0);

      const visibleBottom = Math.min(rect.bottom, window.innerHeight);

      const visibleLeft = Math.max(rect.left, 0);

      const visibleRight = Math.min(rect.right, window.innerWidth);

      const area = Math.max(0, visibleBottom - visibleTop) * Math.max(0, visibleRight - visibleLeft);

      if (area > bestArea) {

        bestArea = area;

        best = node;

      }

    }



    return best;

  }



  private clearTimers(): void {

    if (this.repositionTimer) {

      clearTimeout(this.repositionTimer);

      this.repositionTimer = null;

    }

    if (this.locateTimer) {

      clearTimeout(this.locateTimer);

      this.locateTimer = null;

    }

    // Catalog upload alert temporarily disabled
    /*
    if (this.catalogLeaveTimer) {

      clearTimeout(this.catalogLeaveTimer);

      this.catalogLeaveTimer = null;

    }
    */

  }



  // Catalog upload alert temporarily disabled
  /*
  private onCatalogAlertStatusChange(status: CatalogSyncStatus): void {

    if (this.onboarding.tourActive$.value) {

      this.onboarding.refreshTourGateState();

    }

    if (status === 'loading' || status === 'failed') {

      if (this.catalogLeaveTimer) {

        clearTimeout(this.catalogLeaveTimer);

        this.catalogLeaveTimer = null;

      }

      this.catalogAlertLeaving = false;

      this.catalogAlertDisplayed = true;

      return;

    }

    if (this.catalogAlertDisplayed && !this.catalogAlertLeaving) {

      this.catalogAlertLeaving = true;

      if (this.catalogLeaveTimer) {

        clearTimeout(this.catalogLeaveTimer);

      }

      this.catalogLeaveTimer = setTimeout(() => {

        this.catalogAlertDisplayed = false;

        this.catalogAlertLeaving = false;

        this.catalogLeaveTimer = null;

      }, this.catalogLeaveDurationMs);

    }

  }
  */



  /** Unlock briefly, align anchor, then re-lock so spotlight stays on the right card. */
  private prepareViewportForCurrentStep(): void {

    if (this.onboarding.isActivationTourActive()) {

      return;

    }

    const wasLocked = !!this.scrollLockState;

    if (wasLocked) {

      this.unlockPageScroll();

    }

    this.alignViewportForStep(this.currentStep);

    this.lockPageScroll();

  }



  private alignViewportForStep(step: OnboardingTourStep | null): void {

    if (!step) {

      return;

    }

    const anchor = document.querySelector(step.anchorSelector);

    if (!(anchor instanceof HTMLElement)) {

      return;

    }

    const rect = anchor.getBoundingClientRect();

    const topPadding = 100;

    const bottomReserve = 340;

    const fitsViewport =

      rect.top >= topPadding && rect.bottom <= window.innerHeight - bottomReserve;

    if (fitsViewport) {

      return;

    }

    window.scrollTo({

      top: Math.max(0, window.scrollY + rect.top - topPadding),

      behavior: 'auto',

    });

  }



  private lockPageScroll(): void {

    if (this.onboarding.isActivationTourActive()) {

      return;

    }

    if (this.scrollLockState) {

      return;

    }

    const html = document.documentElement;

    const body = document.body;

    const scrollY = window.scrollY;

    const lockedElements: Array<{ el: HTMLElement; overflow: string }> = [];



    for (const selector of this.scrollContainerSelectors) {

      document.querySelectorAll(selector).forEach((node) => {

        if (!(node instanceof HTMLElement)) {

          return;

        }

        lockedElements.push({ el: node, overflow: node.style.overflow });

        node.style.overflow = 'hidden';

      });

    }



    this.scrollLockState = {

      scrollY,

      htmlOverflow: html.style.overflow,

      bodyOverflow: body.style.overflow,

      bodyPosition: body.style.position,

      bodyTop: body.style.top,

      bodyWidth: body.style.width,

      lockedElements,

    };



    html.classList.add('onboarding-tour-scroll-lock');

    html.style.overflow = 'hidden';

    body.style.overflow = 'hidden';

    body.style.position = 'fixed';

    body.style.top = `-${scrollY}px`;

    body.style.width = '100%';



    document.addEventListener('wheel', this.wheelListener, { passive: false });

    document.addEventListener('touchmove', this.touchMoveListener, { passive: false });

  }



  private unlockPageScroll(): void {

    document.removeEventListener('wheel', this.wheelListener);

    document.removeEventListener('touchmove', this.touchMoveListener);



    if (!this.scrollLockState) {

      return;

    }

    const state = this.scrollLockState;

    const html = document.documentElement;

    const body = document.body;



    html.classList.remove('onboarding-tour-scroll-lock');

    html.style.overflow = state.htmlOverflow;

    body.style.overflow = state.bodyOverflow;

    body.style.position = state.bodyPosition;

    body.style.top = state.bodyTop;

    body.style.width = state.bodyWidth;



    for (const { el, overflow } of state.lockedElements) {

      el.style.overflow = overflow;

    }



    window.scrollTo(0, state.scrollY);

    this.scrollLockState = null;

  }



  private blockScrollEvent(event: Event): void {

    if (!this.showOverlay) {

      return;

    }

    const card = this.tooltipCard?.nativeElement;

    if (card && event.target instanceof Node && card.contains(event.target)) {

      return;

    }

    event.preventDefault();

  }

}


